import React, { useState, useEffect, useCallback } from 'react';
import DocumentCenter from './DocumentCenter';
import { driveService } from '../services/api';
import {
  HardDrive, UploadCloud, Search, ExternalLink, Loader2, FolderOpen, FileText,
  Filter, RefreshCw,
} from 'lucide-react';

const fmtSize = (b) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return ''; } };

const DriveTab = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [domain, setDomain] = useState('');
  const [q, setQ] = useState('');
  const [hideFolders, setHideFolders] = useState(true);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, idx] = await Promise.all([
        stats ? Promise.resolve(stats) : driveService.getStats(),
        driveService.getIndex({ domain: domain || undefined, q: q || undefined, limit: 60 }),
      ]);
      if (!stats) setStats(s);
      let list = idx.items || [];
      if (hideFolders) list = list.filter((it) => !it.is_folder && it.document_type !== 'Folder');
      setItems(list);
      setTotal(idx.total || 0);
      setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, q, hideFolders]);

  useEffect(() => { load(); }, [load]);

  if (disabled) {
    return <div className="text-center py-12 text-gray-500" data-testid="udocs-drive-disabled">Google Drive integration is not active in this environment.</div>;
  }

  const domains = stats?.by_domain ? Object.entries(stats.by_domain).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="space-y-4" data-testid="udocs-drive-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Search Drive documents" data-testid="udocs-drive-search" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="udocs-drive-domain" className="py-2 px-3 text-sm border border-gray-300 rounded-md">
          <option value="">All domains ({stats?.total ?? 0})</option>
          {domains.map(([d, c]) => <option key={d} value={d}>{d} ({c})</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={hideFolders} onChange={(e) => setHideFolders(e.target.checked)} data-testid="udocs-drive-hidefolders" /> Hide folders</label>
        <button onClick={load} data-testid="udocs-drive-refresh" className="inline-flex items-center gap-1 px-2.5 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm" data-testid="udocs-drive-empty">No Drive documents match this filter.</div>
      ) : (
        <>
          <p className="text-xs text-gray-500" data-testid="udocs-drive-count">Showing {items.length} of {total} indexed Google Drive documents (read-only reference).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="udocs-drive-list">
            {items.map((it) => (
              <div key={it.id} data-testid={`udocs-drive-item-${it.id}`} className="border border-gray-200 rounded-lg p-3 flex items-start gap-3 hover:border-gray-300 transition-colors">
                <div className="mt-0.5">{it.is_folder || it.document_type === 'Folder' ? <FolderOpen className="w-4 h-4 text-amber-500" /> : <FileText className="w-4 h-4 text-indigo-500" />}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{it.document_title}</p>
                  <p className="text-[11px] text-gray-500 truncate">{it.folder_location}</p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium">SOURCE: Google Drive</span>
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">{it.operational_domain}</span>
                    <span className="text-[10px] text-gray-400">{it.document_type}{it.size_bytes ? ` · ${fmtSize(it.size_bytes)}` : ''}{it.modified_time ? ` · ${fmtDate(it.modified_time)}` : ''}</span>
                    {it.expiration_date && <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10px]">expires {fmtDate(it.expiration_date)}</span>}
                  </div>
                </div>
                {it.web_view_link && (
                  <a href={it.web_view_link} target="_blank" rel="noopener noreferrer" data-testid={`udocs-drive-link-${it.id}`} className="text-gray-400 hover:text-blue-600 shrink-0" aria-label="Open in Google Drive"><ExternalLink className="w-4 h-4" /></a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const UnifiedDocuments = ({ isAdmin = false }) => {
  const [tab, setTab] = useState('drive');
  const [driveTotal, setDriveTotal] = useState(null);

  useEffect(() => {
    driveService.getStats().then((s) => setDriveTotal(s?.total ?? null)).catch(() => setDriveTotal(null));
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="unified-documents">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6 text-blue-600" /> Documents</h1>
        <p className="text-gray-600 mt-1 text-sm">One place for governed Google Drive documents and direct platform uploads. Drive files are referenced read-only — never copied or modified.</p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        <button onClick={() => setTab('drive')} data-testid="udocs-tab-drive" className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'drive' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <HardDrive className="w-4 h-4" /> Google Drive {driveTotal != null && <span className="text-xs bg-gray-100 rounded-full px-1.5">{driveTotal}</span>}
        </button>
        <button onClick={() => setTab('uploads')} data-testid="udocs-tab-uploads" className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'uploads' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <UploadCloud className="w-4 h-4" /> Platform Uploads
        </button>
      </div>

      {tab === 'drive' ? <DriveTab /> : (
        <div data-testid="udocs-uploads-tab">
          <DocumentCenter title="Platform Uploads" subtitle="Files uploaded directly to BlueLock. Stored securely; access is audit-logged." isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
};

export default UnifiedDocuments;
